import type { ManagementUser } from '@efdigital/ll-admin-v2/data/builders/users/find-user';
import type { LookupUser } from '@efdigital/ll-admin-v2/data/builders/users/lookup-users';
import {
  isManagementUser,
  profileEmailWithFallback,
  type UserProfile,
} from '@efdigital/ll-admin-v2/utils/users';
import { LinkTo } from '@ember/routing';
import Component from '@glimmer/component';

interface Signature {
  Args: {
    profile: UserProfile;
  };
}

export default class UserIdentitySection extends Component<Signature> {
  get email(): string {
    return profileEmailWithFallback(this.args.profile);
  }

  get client(): ManagementUser['client'] | null {
    if (isManagementUser(this.args.profile)) {
      return this.args.profile.client;
    }

    return null;
  }

  get encryptedIds(): string[] {
    if (isManagementUser(this.args.profile)) {
      return this.args.profile.encryptedIds;
    }

    return [];
  }

  get isDeleted(): boolean {
    return Boolean(this.args.profile.deleted);
  }

  get isAnonymous(): boolean {
    const profile = this.args.profile as LookupUser;
    return Boolean(profile.consent?.anon);
  }

  get isInvalidClient(): boolean {
    return this.client?.title === 'Invalid client';
  }

  <template>
    <div class="card mb-4" data-test-user-identity>
      <div class="card-header">
        <h2 class="h5 mb-0">Identity</h2>
      </div>
      <div class="card-body">
        <dl class="row mb-0">
          <dt class="col-sm-3">User ID</dt>
          <dd class="col-sm-9 font-monospace small mb-3">{{@profile.id}}
            {{#if this.isDeleted}}<span
                class="badge text-bg-danger fw-bold"
              >Deleted</span>{{/if}}</dd>

          <dt class="col-sm-3">Email</dt>
          <dd class="col-sm-9 mb-3">{{this.email}}</dd>
        </dl>
        <dl class="row align-items-center mb-0">
          {{#if this.client}}
            <dt class="col-sm-3 mb-0">Client</dt>
            <dd class="col-sm-9 mb-0">
              {{#if this.isInvalidClient}}
                <span class="text-danger"><iconify-icon
                    inline
                    icon="ph:warning"
                  />
                  Invalid client</span>
              {{else}}
                <LinkTo
                  @route="library.clients.client.edit.general"
                  @model={{this.client.id}}
                  class="btn btn-sm btn-outline-primary fw-bold text-nowrap overflow-hidden"
                  data-test-user-client-link
                >
                  {{this.client.title}}
                  <iconify-icon inline icon="ph:arrow-right" />
                </LinkTo>
              {{/if}}
            </dd>
          {{/if}}
        </dl>
      </div>
    </div>
  </template>
}
